/**
 * The player's controls (GDD §8): every input gives the same three actions, steer, throttle and fire, which merge
 * into the one pilot input the simulation reads (GDD §11). Menu and flow commands travel separately.
 */
import type { PilotInput } from '../sim/pilot.ts';

/** One input's share of the controls, updated by its events or its per-frame poll. */
export interface ControlSource {
  /** −1 full left … +1 full right. */
  steer: number;
  /** −1 slowest … 0 cruise … +1 boost. */
  throttle: number;
  fire: boolean;
}

export const createSource = (): ControlSource => ({ steer: 0, throttle: 0, fire: false });

/**
 * Flow and menu commands. `start` starts a run from the title; `pause` toggles the pause (P, Esc, the pause
 * button); `menu` is a gamepad's Start, which also starts a run from the title; `back` leaves a menu (Esc, gamepad
 * B); `confirm` presses the focused menu button (gamepad A); `up`/`down` move the menu focus.
 */
export type Command = 'start' | 'pause' | 'menu' | 'back' | 'confirm' | 'up' | 'down';
export type CommandSink = (cmd: Command) => void;

const clamp1 = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v);

/** Merges every source into `out`: axes add up (clamped), fire is held by any. */
export function mergeControls(out: PilotInput, sources: readonly ControlSource[]): void {
  let steer = 0;
  let throttle = 0;
  let fire = false;
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    if (!s) continue;
    steer += s.steer;
    throttle += s.throttle;
    fire = fire || s.fire;
  }
  out.steer = clamp1(steer);
  out.throttle = clamp1(throttle);
  out.fire = fire;
}

/** Releases every control of a source (focus lost, layout changed). */
export function releaseSource(s: ControlSource): void {
  s.steer = 0;
  s.throttle = 0;
  s.fire = false;
}
